import { useCallback, useState } from 'react';
import {
  CommunicationOption,
  ContentProps,
} from 'models/profile-settings.model';
import {
  sendSettingsUnsubscribeButtonClickedEvent,
  sendAccountSetupUnsubscribeButtonClickedEvent,
  sendAccountSetupConfirmPaperlessButtonClickedEvent,
  sendSettingsConfirmPaperlessButtonClickedEvent,
} from 'utils/analytics-events';

export const useCommunicationModals = (
  options: CommunicationOption[],
  setOptions: (options: CommunicationOption[]) => void,
  setContents: (contents: ContentProps[]) => void,
  formatValue: (options: CommunicationOption[]) => ContentProps[],
  preEditOptions: CommunicationOption[],
  setPreEditOptions: (options: CommunicationOption[]) => void,
  setPendingOptions: (options: CommunicationOption[] | null) => void,
  isAccountSetup?: boolean,
) => {
  const [showPaperlessModal, setShowPaperlessModal] = useState(false);
  const [showUnsubscribeModal, setShowUnsubscribeModal] = useState(false);
  const [pendingPaperless, setPendingPaperless] = useState<boolean | null>(
    null,
  );
  const [pendingUnsubscribe, setPendingUnsubscribe] = useState<boolean | null>(
    null,
  );

  const handlePaperlessConfirm = useCallback(() => {
    if (pendingPaperless !== null) {
      const updatedOptions = options.map((opt) => {
        switch (opt.labelId) {
          case 'STR_PAPERLESS':
            return { ...opt, value: pendingPaperless };
          case 'STR_PERSONALIZED_EMAIL':
            return { ...opt, value: true };
          default:
            return opt;
        }
      });

      if (isAccountSetup) {
        sendAccountSetupConfirmPaperlessButtonClickedEvent('go paperless');
      } else {
        sendSettingsConfirmPaperlessButtonClickedEvent('go paperless');
      }

      setOptions(updatedOptions);
      setContents(formatValue(updatedOptions));
      setPendingOptions(null);
    }

    setShowPaperlessModal(false);
    setPendingPaperless(null);
  }, [
    pendingPaperless,
    options,
    setOptions,
    setContents,
    formatValue,
    setPendingOptions,
    isAccountSetup,
  ]);

  const handlePaperlessCancel = useCallback(() => {
    if (isAccountSetup) {
      sendAccountSetupConfirmPaperlessButtonClickedEvent('cancel');
    } else {
      sendSettingsConfirmPaperlessButtonClickedEvent('cancel');
    }
    setShowPaperlessModal(false);
    setPendingPaperless(null);
    setOptions(preEditOptions);
    setContents(formatValue(preEditOptions));
    setPendingOptions(null);
  }, [
    preEditOptions,
    setOptions,
    setContents,
    formatValue,
    setPendingOptions,
    isAccountSetup,
  ]);

  const handleUnsubscribeConfirm = useCallback(() => {
    if (pendingUnsubscribe !== null) {
      const updatedOptions = options.map((opt) => {
        switch (opt.labelId) {
          case 'STR_PERSONALIZED_EMAIL':
            return { ...opt, value: pendingUnsubscribe };
          case 'STR_PAPERLESS':
            return { ...opt, value: false };
          default:
            return opt;
        }
      });
      if (isAccountSetup) {
        sendAccountSetupUnsubscribeButtonClickedEvent('yes unsubscribe');
      } else {
        sendSettingsUnsubscribeButtonClickedEvent('yes unsubscribe');
      }
      setOptions(updatedOptions);
      setPreEditOptions(updatedOptions);
      setContents(formatValue(updatedOptions));
      setPendingOptions(null);
    }

    setShowUnsubscribeModal(false);
    setPendingUnsubscribe(null);
  }, [
    pendingUnsubscribe,
    options,
    setOptions,
    setPreEditOptions,
    setContents,
    formatValue,
    setPendingOptions,
    isAccountSetup,
  ]);

  const handleUnsubscribeCancel = useCallback(() => {
    if (isAccountSetup) {
      sendAccountSetupUnsubscribeButtonClickedEvent('cancel');
    } else {
      sendSettingsUnsubscribeButtonClickedEvent('cancel');
    }
    setShowUnsubscribeModal(false);
    setPendingUnsubscribe(null);
    setPendingOptions(null);
  }, [setPendingOptions, isAccountSetup]);

  return {
    showPaperlessModal,
    setShowPaperlessModal,
    showUnsubscribeModal,
    setShowUnsubscribeModal,
    pendingPaperless,
    setPendingPaperless,
    pendingUnsubscribe,
    setPendingUnsubscribe,
    handlePaperlessConfirm,
    handlePaperlessCancel,
    handleUnsubscribeConfirm,
    handleUnsubscribeCancel,
  };
};
